import moment from 'moment-timezone';
import { stringify } from 'query-string';
import { MONTHS } from '../constants/constants';

export const parseBracketTimestamp = timestampStr => {
  if (!timestampStr || typeof timestampStr !== 'string') return null;

  const cleanStr = timestampStr.replace(/[[\]]/g, '');
  const [timePart, milliseconds = '0'] = cleanStr.split(',');
  const [hours = '0', minutes = '0', seconds = '0'] = timePart
    .split(':')
    .map(part => part.trim());

  const totalSeconds =
    parseInt(hours, 10) * 3600 +
    parseInt(minutes, 10) * 60 +
    parseInt(seconds, 10) +
    parseInt(milliseconds, 10) / 1000;

  if (Number.isNaN(totalSeconds)) {
    return null;
  }

  return totalSeconds;
};

export function getCookie(name) {
  const cookies = document.cookie.split('; ');

  for (let i = 0; i < cookies.length; i += 1) {
    const [key, value] = cookies[i].split('=');

    if (key === name) {
      return value;
    }
  }
  return null;
}

export function getParams(url) {
  var params = {};
  var parser = document.createElement('a');
  parser.href = url;
  var query = parser.search.substring(1);
  var vars = query.split('&');
  for (var i = 0; i < vars.length; i++) {
    var pair = vars[i].split('=');
    params[pair[0]] = decodeURIComponent(pair[1]);
  }
  return params;
}

export function getTimeSinceEpoch(epochValue) {
  const now = new Date();
  const oneDayInMillis = 1000 * 60 * 60 * 24;
  const lastUsed = new Date(epochValue * 1000); // convert epoch value to milliseconds
  const diffInMillis = Math.abs(now - lastUsed);

  // Check if epoch value is negative
  if (epochValue < 0) {
    return 'Epoch value is negative';
  }

  // Check if epoch value is in the future
  if (epochValue > now.getTime() / 1000) {
    return 'Epoch value is in the future';
  }

  // Check if epoch value is today or yesterday
  if (diffInMillis < 2 * oneDayInMillis) {
    if (diffInMillis < oneDayInMillis) {
      return 'Last used today';
    } else {
      return 'Last used yesterday';
    }
  }

  // Calculate difference in days
  const diffInDays = Math.floor(diffInMillis / oneDayInMillis);
  return `Last used ${diffInDays} days ago`;
}

export function timeStringToEpoch(strTime) {
  const dateObj = new Date();
  dateObj.setHours(strTime.substr(0, strTime.indexOf(':')));
  dateObj.setMinutes(strTime.substr(strTime.indexOf(':') + 1));
  dateObj.setSeconds('0');
  dateObj.setMilliseconds('0');
  const epochTime = dateObj.getTime() / 1000;
  return epochTime;
}

export function dateObjToEpoch(string) {
  const date = new Date(string);
  date.setSeconds('0');
  date.setMilliseconds('0');
  const epochTime = date.getTime() / 1000;
  return epochTime;
}

export function convertEpochToDateFormat(epochDate) {
  let date = new Date(epochDate);
  let day = date
    .getDate()
    .toString()
    .padStart(2, '0');
  let month = date.getMonth().toString();
  let year = date
    .getFullYear()
    .toString()
    .substring(2, 4);
  month = MONTHS[month] && MONTHS[month].substring(0, 3);

  return `${day} ${month} ${year}`;
}

export function convertEpochToDateTime(epochDate) {
  let date = new Date(epochDate);
  let day = date
    .getDate()
    .toString()
    .padStart(2, '0');
  let month = date.getMonth().toString();
  let year = date
    .getFullYear()
    .toString()
    .substring(2, 4);
  month = MONTHS[month] && MONTHS[month].substring(0, 3);

  return `${day} ${month} '${year} ${toTimeString(date)}`;
}

export function formatTimestamp(timestamp) {
  const date = new Date(timestamp * 1000);
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const meridiem = hours >= 12 ? 'PM' : 'AM';
  const formattedHours = hours % 12 || 12;
  const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
  const formattedDate = `${date.toLocaleDateString('en-US', {
    weekday: 'short',
  })} ${formattedHours}:${formattedMinutes}${meridiem}`;
  return formattedDate;
}

export function convertEpochToLocalTime(epochTime, timeZone) {
  const localTime = moment
    ?.unix(epochTime)
    ?.tz(timeZone)
    ?.format('h:mm A');

  return localTime;
}

export function toTimeString(date) {
  return date.toLocaleString('en-us', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function toDateTimeString(date) {
  return date.toLocaleString('en-us', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function to24HrTimeString(date) {
  return date.toLocaleString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export function epochTo24HrTime(epochTime) {
  return to24HrTimeString(new Date(epochTime * 1000));
}

export function epochToTime(epochTime) {
  return toTimeString(new Date(epochTime * 1000));
}

export function epochToDate(epochTime, format) {
  const dateFormat = moment.unix(epochTime).format(format);
  return dateFormat;
}

export function roundToNearestHigher30(date = new Date()) {
  const minutes = 30;
  const ms = 1000 * 60 * minutes;

  return new Date(Math.ceil(date.getTime() / ms) * ms);
}

export function getEndOfWeek() {
  const now = new Date();

  now.setHours(0, 0, 0);
  const dayToday = now.getDay();
  const nextSunday = new Date(now.setDate(now.getDate() - dayToday + 7));

  // if dayToday is sat, return next weeks sunday
  if (dayToday === 6) {
    nextSunday.setDate(nextSunday.getDate() + 7);
  }

  nextSunday.setHours(23, 59, 59);

  return nextSunday;
}

export function addTime(date, minutes = 0, hours = 0) {
  return new Date(date.getTime() + minutes * 60000 + hours * 3600000);
}

export function generate30minSlots(start_time, end_time) {
  let cur = roundToNearestHigher30(start_time);
  const options = [];

  while (cur.valueOf() <= end_time.valueOf()) {
    options.push({ name: toDateTimeString(cur), code: cur.valueOf() / 1000 });
    cur = addTime(cur, 30);
  }

  return options;
}

export function programNameToId(programs, programNames) {
  const programsList = [];
  programs.forEach(program => {
    if (programNames.includes(program.code)) {
      programsList.push(program.id);
    }
  });
  return programsList;
}

export function checkOrder(dataObject, order) {
  console.log('checkOrder', dataObject, order);
  let toggle = true;
  order.forEach((key, i) => {
    if (i < order.length - 1) {
      if (!(dataObject[order[i]] < dataObject[order[i + 1]])) {
        toggle = false;
      }
    }
  });
  return toggle;
}

export function filterOptionsLabel(options, value) {
  const [{ label = '' } = {}] = options.filter(item => item.value === value) || [];
  return label;
}

export function labelJoinAnd(data) {
  let toggleCheckUnlockList = [];
  let labelListAnd = data.map(row => {
    if (!row.checkUnlock) {
      toggleCheckUnlockList.push(false);
    } else {
      toggleCheckUnlockList.push(true);
    }
    if (row.parentCompletion === 1 || row.parentCompletion === 0) {
      return 'Parent Completion:' + row.parentCompletion;
    }
    return row.attribute + ' ' + row.operator + ' ' + row.attributeValue;
  });
  return {
    label: labelListAnd.join(' \n '),
    checkUnlock: toggleCheckUnlockList,
  };
}

export function labelBuilder(data) {
  let labelList = [];
  let checkUnlockList = [];
  data.forEach(block => {
    let blockLabel = labelJoinAnd(block);
    labelList.push(blockLabel.label);
    checkUnlockList.push(...blockLabel.checkUnlock);
  });
  let labelString = labelList.join(' \n OR \n ');
  return { labelString, checkUnlockList };
}

export function slotCreationRestriction() {
  const date = new Date();
  const hours = date.getHours();
  const minutes = date.getMinutes();

  if ((hours === 23 && minutes >= 30) || (hours === 0 && minutes <= 5)) {
    return true;
  }

  return false;
}

export const COUNTRIES_MAP = {
  AU: { code: '61', name: 'Australia', price: 100 },
  CA: { code: '1', name: 'Canada', price: 100 },
  IN: { code: '91', icon: '🇮🇳', name: 'India', price: 2000 },
  TH: { code: '66', icon: '🇹🇭', name: 'Thailand', price: 1000 },
  AE: {
    code: '971',
    name: 'United Ar',
    price: 150,
  },
  US: { code: '1', name: 'United States' },
  AF: { code: '93', name: 'Afghanistan' },
  AX: { code: '358', name: 'Aland Islands' },
  AL: { code: '355', name: 'Albania' },
  DZ: { code: '213', name: 'Algeria' },
  AD: { code: '376', name: 'Andorra' },
  AO: { code: '244', name: 'Angola' },
  AI: { code: '1264', name: 'Anguilla' },
  AG: { code: '1268', name: 'Antigua and Barbuda' },
  AR: { code: '54', name: 'Argentina' },
  AM: { code: '374', name: 'Armenia' },
  AW: { code: '297', name: 'Aruba' },
  AT: { code: '43', name: 'Austria' },
  AZ: { code: '994', name: 'Azerbaijan' },
  BS: { code: '1242', name: 'Bahamas' },
  BH: { code: '973', name: 'Bahrain' },
  BD: { code: '880', name: 'Bangladesh' },
  BY: { code: '375', name: 'Belarus' },
  BE: { code: '32', name: 'Belgium' },
  BZ: { code: '501', name: 'Belize' },
  BJ: { code: '229', name: 'Benin' },
  BM: { code: '1441', name: 'Bermuda' },
  BT: { code: '975', name: 'Bhutan' },
  BO: { code: '591', name: 'Bolivia, Plurinational State of bolivia' },
  BA: { code: '387', name: 'Bosnia and Herzegovina' },
  BW: { code: '267', name: 'Botswana' },
  BV: { code: '47', name: 'Bouvet Island' },
  BR: { code: '55', name: 'Brazil' },
  BN: { code: '673', name: 'Brunei Darussalam' },
  BG: { code: '359', name: 'Bulgaria' },
  BF: { code: '226', name: 'Burkina Faso' },
  BI: { code: '257', name: 'Burundi' },
  KH: { code: '855', name: 'Cambodia' },
  CM: { code: '237', name: 'Cameroon' },
  CV: { code: '238', name: 'Cape Verde' },
  CF: { code: '236', name: 'Central African Republic' },
  TD: { code: '235', name: 'Chad' },
  CL: { code: '56', name: 'Chile' },
  CN: { code: '86', name: 'China' },
  CX: { code: '61', name: 'Christmas Island' },
  CC: { code: '61', name: 'Cocos Keeling Islands' },
  CO: { code: '57', name: 'Colombia' },
  KM: { code: '269', name: 'Comoros' },
  CG: { code: '242', name: 'Congo' },
  CD: { code: '243', name: 'Congo, The Democratic Republic of the Congo' },
  CK: { code: '682', name: 'Cook Islands' },
  CR: { code: '506', name: 'Costa Rica' },
  CI: { code: '225', name: "Cote d'Ivoire" },
  HR: { code: '385', name: 'Croatia' },
  CU: { code: '53', name: 'Cuba' },
  CY: { code: '357', name: 'Cyprus' },
  CZ: { code: '420', name: 'Czech Republic' },
  DK: { code: '45', name: 'Denmark' },
  DJ: { code: '253', name: 'Djibouti' },
  EC: { code: '593', name: 'Ecuador' },
  EG: { code: '20', name: 'Egypt' },
  SV: { code: '503', name: 'El Salvador' },
  GQ: { code: '240', name: 'Equatorial Guinea' },
  ER: { code: '291', name: 'Eritrea' },
  EE: { code: '372', name: 'Estonia' },
  ET: { code: '251', name: 'Ethiopia' },
  FK: { code: '500', name: 'Falkland Islands Malvinas' },
  FO: { code: '298', name: 'Faroe Islands' },
  FJ: { code: '679', name: 'Fiji' },
  FI: { code: '358', name: 'Finland' },
  FR: { code: '33', name: 'France' },
  GF: { code: '594', name: 'French Guiana' },
  PF: { code: '689', name: 'French Polynesia' },
  TF: { code: '262', name: 'French Southern Territories' },
  GA: { code: '241', name: 'Gabon' },
  GM: { code: '220', name: 'Gambia' },
  GE: { code: '995', name: 'Georgia' },
  DE: { code: '49', name: 'Germany' },
  GH: { code: '233', name: 'Ghana' },
  GI: { code: '350', name: 'Gibraltar' },
  GR: { code: '30', name: 'Greece' },
  GU: { code: '1671', name: 'Guam' },
  GT: { code: '502', name: 'Guatemala' },
  GG: { code: '44', name: 'Guernsey' },
  GN: { code: '224', name: 'Guinea' },
  GW: { code: '245', name: 'Guinea-Bissau' },
  GY: { code: '592', name: 'Guyana' },
  HT: { code: '509', name: 'Haiti' },
  HM: { code: '672', name: 'Heard Island and Mcdonald Islands' },
  HN: { code: '504', name: 'Honduras' },
  HK: { code: '852', name: 'Hong Kong' },
  HU: { code: '36', name: 'Hungary' },
  IS: { code: '354', name: 'Iceland' },
  ID: { code: '62', name: 'Indonesia' },
  IR: { code: '98', name: 'Iran, Islamic Republic of Persian Gulf' },
  IQ: { code: '964', name: 'Iraq' },
  IE: { code: '353', name: 'Ireland' },
  IM: { code: '44', name: 'Isle of Man' },
  IL: { code: '972', name: 'Israel' },
  IT: { code: '39', name: 'Italy' },
  JP: { code: '81', name: 'Japan' },
  JE: { code: '44', name: 'Jersey' },
  JO: { code: '962', name: 'Jordan' },
  KZ: { code: '7', iame: 'Kazakhstan' },
  KE: { code: '254', name: 'Kenya' },
  KR: { code: '82', name: 'Korea, Republic of South Korea' },
  KW: { code: '965', name: 'Kuwait' },
  KG: { code: '996', name: 'Kyrgyzstan' },
  LA: { code: '856', name: 'Laos' },
  LV: { code: '371', name: 'Latvia' },
  LB: { code: '961', name: 'Lebanon' },
  LS: { code: '266', name: 'Lesotho' },
  LR: { code: '231', name: 'Liberia' },
  LY: { code: '218', name: 'Libyan Arab Jamahiriya' },
  LI: { code: '423', name: 'Liechtenstein' },
  LT: { code: '370', name: 'Lithuania' },
  LU: { code: '352', name: 'Luxembourg' },
  MO: { code: '853', name: 'Macao' },
  MK: { code: '389', name: 'Macedonia' },
  MG: { code: '261', name: 'Madagascar' },
  MW: { code: '265', name: 'Malawi' },
  MY: { code: '60', name: 'Malaysia' },
  MV: { code: '960', name: 'Maldives' },
  ML: { code: '223', name: 'Mali' },
  MT: { code: '356', name: 'Malta' },
  MR: { code: '222', name: 'Mauritania' },
  MU: { code: '230', name: 'Mauritius' },
  YT: { code: '262', name: 'Mayotte' },
  MX: { code: '52', name: 'Mexico' },
  MD: { code: '373', name: 'Moldova' },
  MC: { code: '377', name: 'Monaco' },
  MN: { code: '976', name: 'Mongolia' },
  ME: { code: '382', name: 'Montenegro' },
  MA: { code: '212', name: 'Morocco' },
  MZ: { code: '258', name: 'Mozambique' },
  MM: { code: '95', name: 'Myanmar' },
  NA: { code: '264', name: 'Namibia' },
  NP: { code: '977', name: 'Nepal' },
  NL: { code: '31', name: 'Netherlands' },
  NC: { code: '687', name: 'New Caledonia' },
  NZ: { code: '64', name: 'New Zealand' },
  NI: { code: '505', name: 'Nicaragua' },
  NE: { code: '227', name: 'Niger' },
  NG: { code: '234', name: 'Nigeria' },
  NF: { code: '672', name: 'Norfolk Island' },
  NO: { code: '47', name: 'Norway' },
  OM: { code: '968', name: 'Oman' },
  PK: { code: '92', name: 'Pakistan' },
  PS: { code: '970', name: 'Palestinian Territory, Occupied' },
  PA: { code: '507', name: 'Panama' },
  PG: { code: '675', name: 'Papua New Guinea' },
  PY: { code: '595', name: 'Paraguay' },
  PE: { code: '51', name: 'Peru' },
  PH: { code: '63', name: 'Philippines' },
  PN: { code: '64', name: 'Pitcairn' },
  PL: { code: '48', name: 'Poland' },
  PT: { code: '351', name: 'Portugal' },
  QA: { code: '974', name: 'Qatar' },
  RE: { code: '262', name: 'Reunion' },
  RO: { code: '40', name: 'Romania' },
  RU: { code: '7', name: 'Russia' },
  RW: { code: '250', name: 'Rwanda' },
  BL: { code: '590', name: 'Saint Barthelemy' },
  MF: { code: '590', name: 'Saint Martin' },
  SM: { code: '378', name: 'San Marino' },
  ST: { code: '239', name: 'Sao Tome and Principe' },
  SA: { code: '966', name: 'Saudi Arabia' },
  SN: { code: '221', name: 'Senegal' },
  RS: { code: '381', name: 'Serbia' },
  SC: { code: '248', name: 'Seychelles' },
  SL: { code: '232', name: 'Sierra Leone' },
  SG: { code: '65', name: 'Singapore' },
  SK: { code: '421', name: 'Slovakia' },
  SI: { code: '386', name: 'Slovenia' },
  SO: { code: '252', name: 'Somalia' },
  ZA: { code: '27', name: 'South Africa' },
  SS: { code: '211', name: 'South Sudan' },
  ES: { code: '34', name: 'Spain' },
  LK: { code: '94', name: 'Sri Lanka' },
  SD: { code: '249', name: 'Sudan' },
  SR: { code: '597', name: 'Suriname' },
  SJ: { code: '47', name: 'Svalbard and Jan Mayen' },
  SZ: { code: '268', name: 'Swaziland' },
  SE: { code: '46', name: 'Sweden' },
  CH: { code: '41', name: 'Switzerland' },
  SY: { code: '963', name: 'Syrian Arab Republic' },
  TW: { code: '886', name: 'Taiwan' },
  TJ: { code: '992', name: 'Tajikistan' },
  TZ: { code: '255', name: 'Tanzania, United Republic of Tanzania' },
  TL: { code: '670', name: 'Timor-Leste' },
  TG: { code: '228', name: 'Togo' },
  TO: { code: '676', name: 'Tonga' },
  TN: { code: '216', name: 'Tunisia' },
  TR: { code: '90', name: 'Turkey' },
  TM: { code: '993', name: 'Turkmenistan' },
  UG: { code: '256', name: 'Uganda' },
  UA: { code: '380', name: 'Ukraine' },
  GB: { code: '44', name: 'United Kingdom' },
  UY: { code: '598', name: 'Uruguay' },
  UZ: { code: '998', name: 'Uzbekistan' },
  VU: { code: '678', name: 'Vanuatu' },
  VE: { code: '58', name: 'Venezuela, Bolivarian Republic of Venezuela' },
  VN: { code: '84', name: 'Vietnam' },
  YE: { code: '967', name: 'Yemen' },
  ZM: { code: '260', name: 'Zambia' },
  ZW: { code: '263', name: 'Zimbabwe' },
};

export const INDIAN_STATE_OPTIONS = [
  { value: 'Andhra Pradesh', label: 'Andhra Pradesh' },
  { value: 'Andaman and Nicobar Islands', label: 'Andaman and Nicobar Islands' },
  { value: 'Arunachal Pradesh', label: 'Arunachal Pradesh' },
  { value: 'Assam', label: 'Assam' },
  { value: 'Bihar', label: 'Bihar' },
  { value: 'Chandigarh', label: 'Chandigarh' },
  { value: 'Chhattisgarh', label: 'Chhattisgarh' },
  { value: 'Dadar and Nagar Haveli', label: 'Dadar and Nagar Haveli' },
  { value: 'Daman and Diu', label: 'Daman and Diu' },
  { value: 'Delhi', label: 'Delhi' },
  { value: 'Lakshadweep', label: 'Lakshadweep' },
  { value: 'Puducherry', label: 'Puducherry' },
  { value: 'Goa', label: 'Goa' },
  { value: 'Gujarat', label: 'Gujarat' },
  { value: 'Haryana', label: 'Haryana' },
  { value: 'Himachal Pradesh', label: 'Himachal Pradesh' },
  { value: 'Jammu and Kashmir', label: 'Jammu and Kashmir' },
  { value: 'Jharkhand', label: 'Jharkhand' },
  { value: 'Karnataka', label: 'Karnataka' },
  { value: 'Kerala', label: 'Kerala' },
  { value: 'Madhya Pradesh', label: 'Madhya Pradesh' },
  { value: 'Maharashtra', label: 'Maharashtra' },
  { value: 'Manipur', label: 'Manipur' },
  { value: 'Meghalaya', label: 'Meghalaya' },
  { value: 'Mizoram', label: 'Mizoram' },
  { value: 'Nagaland', label: 'Nagaland' },
  { value: 'Odisha', label: 'Odisha' },
  { value: 'Punjab', label: 'Punjab' },
  { value: 'Rajasthan', label: 'Rajasthan' },
  { value: 'Sikkim', label: 'Sikkim' },
  { value: 'Tamil Nadu', label: 'Tamil Nadu' },
  { value: 'Telangana', label: 'Telangana' },
  { value: 'Tripura', label: 'Tripura' },
  { value: 'Uttar Pradesh', label: 'Uttar Pradesh' },
  { value: 'Uttarakhand', label: 'Uttarakhand' },
  { value: 'West Bengal', label: 'West Bengal' },
];

export const GRADE_OPTIONS = [
  { value: '-1', label: '-1' },
  { value: '0', label: '0' },
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: '4', label: '4' },
  { value: '5', label: '5' },
  { value: '6', label: '6' },
  { value: '7', label: '7' },
  { value: '8', label: '8' },
];

export const COUNTRY_OPTIONS_MAP = Object.keys(COUNTRIES_MAP).map(key => ({
  id: key,
  ...COUNTRIES_MAP[key],
}));

export const COUNTRY_OPTIONS = COUNTRY_OPTIONS_MAP.map(({ name, code }) => ({
  label: `${name}-${code}`,
  value: code,
}));

export const COUNTRY_OPTIONS_V2 = Object.keys(COUNTRIES_MAP).map(key => ({
  id: key,
  label: COUNTRIES_MAP[key].name,
  value: key,
}));

export const BILLING_COUNTRY_OPTIONS = COUNTRY_OPTIONS_MAP.filter(
  ({ name }) => name !== 'India',
).map(({ name }) => ({
  label: name,
  value: name,
}));

export const capitalizeFirstLetter = string => {
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};

export const capitalizeAll = string => {
  return string.toUpperCase();
};

export const courseRequiredSlots = ['REGULAR', 'EXTRA'];

export function isValidPincodeIndia(pincode) {
  const pattern = new RegExp('^[1-9][0-9]{5}$');

  return pattern.test(pincode);
}

export const fetchUniqueOptions = data => {
  return data.filter((value, index, array) => array.indexOf(value) === index);
};

export const hideBreadcrumbs = () => {
  const routes = [
    'student-demo-room',
    'admission-tool',
    'support-agent',
    'support-queue',
  ];
  return routes.some(route => window.location.href.includes(route));
};

export const hideAppHeader = () => {
  const routes = ['admission-tool'];
  return routes.some(route => window.location.href.includes(route));
};

export const TOGGLED_RM_ENABLED_GK_PAYLOAD = stringify({
  gk_name: 'toggled_rm_enabled',
  username: '',
});

export const UPGRADE_ADMIN_AUTHENTICATION_GK_PAYLOAD = stringify({
  gk_name: 'upgrade_admin_authentication',
  username: '',
});

export const checkButtonStatus = obj => {
  for (let key in obj) {
    if (obj[key] === '') {
      return true;
    }
  }
  return false;
};

export const copyToClipboard = text => {
  if (!navigator.clipboard) {
    fallbackCopyTextToClipboard(text);
  } else {
    navigator.clipboard.writeText(text);
  }
};

const fallbackCopyTextToClipboard = text => {
  const textArea = document.createElement('textarea');

  textArea.value = text;

  textArea.style.top = '0';
  textArea.style.left = '0';
  textArea.style.position = 'fixed';

  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  try {
    document.execCommand('copy');
  } catch (err) {}

  document.body.removeChild(textArea);

  return null;
};

export function getFormatedDate(dateTimeStamp) {
  const date = new Date(dateTimeStamp * 1000);
  return date.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: '2-digit',
  });
}

export function getFormattedDateWithDay(dateTimestamp) {
  const date = new Date(dateTimestamp * 1000);
  return `${date.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: '2-digit',
  })}, ${date.toLocaleString('en-IN', {
    weekday: 'short',
  })}`;
}

export function getTimeDistance(startTimestamp, endTimestamp) {
  const startTime = new Date(startTimestamp * 1000);
  const endTime = new Date(endTimestamp * 1000);

  const sh = (startTime.getHours() + 24) % 12 || 12;
  const sm = startTime.getMinutes();

  const eh = (endTime.getHours() + 24) % 12 || 12;
  const em = endTime.getMinutes();

  return `${sh}:${sm < 10 ? '0' + sm : sm} ${
    startTime.getHours() > 12 ? 'PM' : 'AM'
  } - ${eh}:${em < 10 ? '0' + em : em} ${endTime.getHours() > 12 ? 'PM' : 'AM'}`;
}

const debounceFunction = () => {
  let debounceTimer = null;
  return (func, delay = 600) => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      if (func) func();
    }, delay);
  };
};

const debouncer = debounceFunction();

export { debouncer };

export const getFormattedDate = (date, format = 'YYYY-MM-DD') => {
  return moment(date).format(format);
};

export const formatErrorMessage = error => {
  const defaultMessage = 'An error occurred:';

  if (typeof error !== 'object' && typeof error === 'string') {
    return `${defaultMessage}\n${error}`;
  }

  return Object.keys(error).reduce((formattedError, key) => {
    return `${formattedError}\n${key}: ${error[key]}`;
  }, defaultMessage);
};

export const getStudentFirstName = ({ firstName, fullName }) => {
  return (
    firstName || (typeof fullName === 'string' && fullName?.split(' ')[0]) || 'Champ'
  );
};

export const getRestAPIGetErrorMessage = resource => {
  const { __ERROR__ } = resource || {};
  const { message } = __ERROR__ || {};

  return message || '';
};

export const getUserFullName = (firstName, lastName) =>
  `${firstName ?? ''} ${lastName ?? ''}`.trim();

export const isSameDate = (timestamp1, timestamp2) => {
  const date1 = new Date(timestamp1 * 1000);
  const date2 = new Date(timestamp2 * 1000);

  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

export const getEndOfDayTimestamp = date => {
  const timestampInSeconds = Math.floor(new Date(`${date}T23:59:59`).getTime() / 1000);

  return timestampInSeconds;
};

/**
 * Generate initials from a name string
 * @param {string} name - The full name to generate initials from
 * @returns {string} - The initials (2 characters, uppercase)
 */
export const getInitials = name => {
  if (!name || typeof name !== 'string') {
    return '??';
  }

  const names = name.trim().split(' ');
  if (names.length >= 2) {
    // For multiple names, use first letter of first and last name
    return (names[0][0] + names[names.length - 1][0]).toUpperCase();
  }
  // For single name, use first 2 characters
  return name.substring(0, 2).toUpperCase();
};

export const downloadTextFile = (filename, text) => {
  const blob = new Blob([text], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 100);
};

/**
 * Format seconds into MM:SS or H:MM:SS format
 * @param {number} seconds - Time in seconds
 * @param {boolean} includeHours - Whether to include hours in the format (default: false)
 * @returns {string} - Formatted time string (MM:SS or H:MM:SS)
 */
export const formatTime = (seconds, includeHours = false) => {
  if (seconds === null || seconds === undefined || isNaN(seconds)) {
    return '--:--';
  }

  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (includeHours || hours > 0) {
    return `${hours}:${mins.toString().padStart(2, '0')}:${secs
      .toString()
      .padStart(2, '0')}`;
  } else {
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
};
